import { first, isNil } from 'lodash';
import { CSSProperties, useCallback, useContext } from 'react';
import { Icon } from '@shared/components/Icon';
import { Text } from '@shared/components/Text';
import { ButtonView, View } from '@shared/components/View';
import { AuditCheckboxField } from '@shared/data/entity/fields/AuditCheckboxField';
import { getComponentLabel } from '@shared/scenes/workflows/utils';
import { ColorThemeContext, Colors, getAnchorName } from '@shared/styles';
import { API } from '@shared/types/api';
import { FileUpload } from '@shared/types/fieldValues';
import { useFileUpload } from '@shared/util/hooks';
import { AuditFieldFooterActions } from './AuditFieldFooterActions';
import { AuditFieldMedia } from './AuditFieldMedia';
import { EditFieldContainer } from './EditFieldContainer';
import styles from './AuditCheckboxEditField.module.css';

interface Props {
  field: AuditCheckboxField | null;
  component: API.AuditCheckboxComponentFragment;
  updateField?: (componentId: string, field: AuditCheckboxField | null) => void;
  error?: string;
  style?: CSSProperties;
}

export function AuditCheckboxEditField({ field, component, updateField, error, style }: Props) {
  const colors = useContext(ColorThemeContext);
  const anchorName = getAnchorName('audit-checkbox-edit-field', component.id);
  const isReadOnly = component.readonly || isNil(updateField);

  const checked = field?.checked ?? false;

  const handleToggle = useCallback(() => {
    updateField?.(
      component.id,
      new AuditCheckboxField({
        checked: !checked,
        files: field?.files ?? [],
        notes: field?.notes ?? '',
      }),
    );
  }, [component.id, updateField, checked, field]);

  const onNotesChange = useCallback(
    (updatedNotes: string) => {
      updateField?.(
        component.id,
        new AuditCheckboxField({ checked, files: field?.files ?? [], notes: updatedNotes }),
      );
    },
    [component.id, updateField, checked, field],
  );

  const onFileAdded = useCallback(
    (addedFiles: FileUpload[]) => {
      const file = first(addedFiles);
      if (!file) {
        return;
      }
      updateField?.(
        component.id,
        new AuditCheckboxField({
          checked,
          files: [...(field?.files ?? []), file],
          notes: field?.notes ?? '',
        }),
      );
    },
    [component.id, updateField, checked, field],
  );

  const onFileRemoved = useCallback(
    (file: FileUpload) => {
      updateField?.(
        component.id,
        new AuditCheckboxField({
          checked,
          files: (field?.files ?? []).filter(f => f.url !== file.url),
          notes: field?.notes ?? '',
        }),
      );
    },
    [component.id, updateField, checked, field],
  );

  const { showFilePicker, uploading } = useFileUpload({
    onFileAdded,
    onFileRemoved,
    multiple: false,
  });

  const checkboxText = getComponentLabel(component);

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      showIcon={false}
      showLabel={false}
      error={error}
      style={style}
      notes={
        !!field?.notes && (
          <Text size="body2" color={colors.foregroundActive}>
            {field.notes}
          </Text>
        )
      }
      files={
        (field?.files ?? []).length > 0 && (
          <AuditFieldMedia
            files={field?.files ?? []}
            editable={!isReadOnly}
            onFileRemoved={onFileRemoved}
          />
        )
      }
      footer={
        !isReadOnly && (
          <AuditFieldFooterActions
            showFilePicker={showFilePicker}
            uploading={uploading}
            notes={field?.notes ?? ''}
            onNotesChange={onNotesChange}
          />
        )
      }
    >
      <ButtonView
        onClick={handleToggle}
        disabled={isReadOnly}
        className={styles.toggleRow}
        center
        row
      >
        <View className={styles.toggleIcon}>
          {checked && <Icon name="check-filled" color={colors.accent} gridSize={4} />}
          {!checked && (
            <Icon
              name="check-task"
              color={!isNil(error) ? Colors.v2.gray30 : colors.input.borderInactive}
              gridSize={4}
            />
          )}
        </View>
        <View flex>
          <Text size="body1" color={colors.foregroundActive}>
            {checkboxText}
          </Text>
        </View>
      </ButtonView>
    </EditFieldContainer>
  );
}
